import React, { useState } from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text } from "@xsolla/xui-primitives";
import {
  useResolvedTheme,
  radiusScale,
  type ThemeOverrideProps,
} from "@xsolla/xui-core";

export interface RadioProps extends ThemeOverrideProps {
  /** Size of the radio button */
  size?: "sm" | "md" | "lg" | "xl";
  /** Whether the radio button is checked */
  checked?: boolean;
  /** Value of the radio button (for RadioGroup) */
  value?: string;
  /** Visual state of the radio button */
  state?: "default" | "hover" | "disable" | "error";
  /** Whether the radio button is disabled */
  disabled?: boolean;
  /** Label text to display next to the radio button */
  label?: string;
  /** Description text to display below the label */
  description?: string;
  /** Error label text to display when state is 'error' */
  errorLabel?: string;
  /** Callback when the radio button is pressed */
  onPress?: () => void;
  /** Callback when the radio button value changes (for compatibility with Checkbox-like APIs) */
  onValueChange?: (value: boolean) => void;
  /** Accessible label for screen readers (if label is not provided) */
  "aria-label"?: string;
  /** ID of element that labels this radio */
  "aria-labelledby"?: string;
  testID?: string;
}

// Mocking useRadioGroup here to avoid circular dependency in local dev
// In a real monorepo this might be handled via a shared types or core package
declare const require: (id: string) => any;
const useRadioGroup = () => {
  try {
    return require("@xsolla/xui-radio-group").useRadioGroup();
  } catch {
    return {};
  }
};

export const Radio: React.FC<RadioProps> = ({
  size: propSize,
  checked: propChecked,
  value,
  state = "default",
  disabled = false,
  label,
  description,
  errorLabel,
  onPress,
  onValueChange,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  testID,
  themeMode,
  themeProductContext,
}) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });
  const radioGroup = useRadioGroup();

  const size = propSize || radioGroup.size || "md";
  const isControlled =
    propChecked !== undefined ||
    (radioGroup.value !== undefined && value !== undefined);
  const [internalChecked, setInternalChecked] = useState(false);
  const checked = isControlled
    ? propChecked !== undefined
      ? propChecked
      : radioGroup.value === value
    : internalChecked;
  const isDisable = state === "disable" || disabled || radioGroup.disabled;
  const isError = state === "error";
  const isHover = state === "hover";
  const showErrorLabel = isError && errorLabel;

  const sizing = theme.sizing.radio(size);

  const checkColors = theme.colors.control.check;
  const textColors = theme.colors.control.text;

  const handlePress = () => {
    if (!isDisable) {
      if (!isControlled) {
        setInternalChecked(true);
      }
      if (onPress) {
        onPress();
      }
      if (radioGroup.onChange && value !== undefined) {
        radioGroup.onChange(value);
      }
      if (onValueChange) {
        onValueChange(!checked);
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isDisable && event.key === " ") {
      event.preventDefault(); // Prevent scrolling on Space
      handlePress();
    }
  };

  const getBgColor = () => {
    if (isDisable) return checkColors.bgDisable;
    if (isHover) {
      return checked ? checkColors.bgHover : theme.colors.control.faint.bgHover;
    }
    if (checked) return checkColors.bg;
    return theme.colors.control.faint.bg;
  };

  const getBorderColor = () => {
    if (isDisable) return checkColors.borderDisable;
    if (isHover) {
      if (checked) return checkColors.borderHover;
      if (isError) return theme.colors.border.alert;
      return theme.colors.control.faint.borderHover;
    }
    if (checked) return checkColors.border;
    if (isError) return theme.colors.border.alert;
    return theme.colors.control.faint.border;
  };

  const getTextColor = () => {
    if (isDisable) return textColors.disable;
    return textColors.primary;
  };

  const getDotColor = () => {
    if (isDisable) return textColors.disable;
    return checkColors.icon;
  };

  const dotSize = sizing.iconSize;

  const getHoverStyle = () => {
    if (isDisable) return undefined;

    if (checked) {
      return {
        backgroundColor: checkColors.bgHover,
        borderColor: checkColors.borderHover,
      };
    } else if (isError) {
      return {
        backgroundColor: theme.colors.control.faint.bgHover,
        borderColor: theme.colors.border.alert,
      };
    } else {
      return {
        backgroundColor: theme.colors.control.faint.bgHover,
        borderColor: theme.colors.control.faint.borderHover,
      };
    }
  };

  return (
    <Box
      testID={testID}
      role="radio"
      aria-checked={checked}
      aria-disabled={isDisable || undefined}
      aria-invalid={isError || undefined}
      aria-label={
        ariaLabelledBy
          ? undefined
          : ariaLabel || (label ? undefined : "Radio button")
      }
      aria-labelledby={ariaLabelledBy}
      tabIndex={isDisable ? -1 : 0}
      flexDirection="row"
      alignItems={description || showErrorLabel ? "flex-start" : "center"}
      gap={sizing.labelGap}
      onPress={handlePress}
      onKeyDown={handleKeyDown}
      cursor={!isDisable ? "pointer" : "default"}
    >
      <Box
        width={sizing.size}
        height={sizing.size}
        backgroundColor={getBgColor()}
        borderColor={getBorderColor()}
        borderWidth={sizing.borderWidth}
        borderRadius={radiusScale["999"]}
        alignItems="center"
        justifyContent="center"
        hoverStyle={getHoverStyle()}
      >
        {checked && (
          <Box
            width={dotSize}
            height={dotSize}
            backgroundColor={getDotColor()}
            borderRadius={radiusScale["999"]}
          />
        )}
      </Box>

      {(label || description || showErrorLabel) && (
        <Box flexDirection="column" gap={sizing.textGap}>
          {label && (
            <Text
              color={getTextColor()}
              fontSize={sizing.fontSize}
              lineHeight={sizing.lineHeight}
              fontWeight="400"
            >
              {label}
            </Text>
          )}
          {description && (
            <Text
              color={theme.colors.content.tertiary}
              fontSize={sizing.descriptionFontSize}
              lineHeight={sizing.descriptionLineHeight}
            >
              {description}
            </Text>
          )}
          {showErrorLabel && (
            <Text
              color={theme.colors.content.alert.primary}
              fontSize={sizing.descriptionFontSize}
              lineHeight={sizing.descriptionLineHeight}
            >
              {errorLabel}
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
};
